import { RestoreFocusAfterUpdate } from "@amx/ux-twoui-utils";
import React from "react";
import { TwoUIOperationStatus, useTwoUIOperationStates } from "../../TwoUIOperationProvider/TwoUIOperationProvider";

export type TwoUIProgressViewType = React.FC<TwoUIProgressViewProps>;

/**
 * Props used by ProgressView that are specific to the monitored operation.
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface ProgressViewOperationData from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export interface TwoUIProgressViewOperationData {
    /**
     * Name of component to be monitored.
     */
    readonly componentName: string;
    /**
     * Name of operation to be monitored. Must match name provided to dispatch
     */
    readonly operationName: string;
    /**
     * Default state for initializing the ProgressView when an operation is not in progress. Defaults to inert.
     */
    readonly defaultState?: Extract<TwoUIOperationStatus, "inert" | "finished">;
}

/**
 * Props used by ProgressView
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface ProgressViewProps from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export interface TwoUIProgressViewProps {
    /** ProgressView does not accept children (setting the type to undefined overrides component defaults) */
    readonly children?: undefined;

    /**
     * Information about the monitored operation.
     */
    readonly operationData: TwoUIProgressViewOperationData;
    /**
     * Functional component to be rendered when operation is inert
     */
    readonly inertView?: JSX.Element;
    /**
     * Functional component to be rendered when operation is loading
     *
     * If set, ProgressView will record the focused element within the view when an update operation starts. After the
     * operation completes, it will try to restore focus unless the user has navigated elsewhere during the update.
     * If not provided, updates will render the finishedView.
     */
    readonly updatingView?: JSX.Element;
    /**
     * Functional component to be rendered when operation is loading for the first time
     */
    readonly firstLoadView?: JSX.Element;
    /**
     * Functional component to be rendered when an update operation fails. Optional. If not provided, failures will render the failedView.
     */
    readonly updateFailedView?: JSX.Element;
    /**
     * Functional component to be rendered when operation completes successfully
     */
    readonly finishedView: JSX.Element;
    /**
     * Functional component to be rendered when operation fails
     */
    readonly failedView?: JSX.Element;
}

export const TwoUIBasicProgressView: React.FC<TwoUIProgressViewProps> = (props) => {
    const operationStates = useTwoUIOperationStates();
    if (props.updatingView) {
        const operationStatus = operationStates[props.operationData.operationName]?.status;
        return (
            <RestoreFocusAfterUpdate isUpdating={operationStatus === "updating"}>
                <ProgressViewInternal {...props} />
            </RestoreFocusAfterUpdate>
        );
    } else {
        return <ProgressViewInternal {...props} />;
    }
};

/**
 * Component for rendering different views
 * depending on status of a tracked operation
 */
const ProgressViewInternal: TwoUIProgressViewType = ({
    operationData,
    finishedView,
    failedView = null,
    firstLoadView = null,
    inertView = null,
    updateFailedView = failedView,
    updatingView = finishedView
}) => {
    const operationStates = useTwoUIOperationStates();
    const operationStatus = operationStates[operationData.operationName];
    const status = operationStatus?.status;

    // if status is not set, pick the right view to render.
    if (!status) {
        // if a default state has been specified, return that without triggering any client telemetry code.
        if (operationData.defaultState === "finished") {
            return finishedView;
        }

        return inertView;
    }

    switch (status) {
        case "updating":
            return updatingView;
        case "first-load":
            return firstLoadView;
        case "finished":
            return finishedView;
        case "update-failed":
            return updateFailedView;
        case "failed":
            return failedView;
        case "inert":
        default:
            return inertView;
    }
};
